import "../assets/css/navigation.css";
import { useState, useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useActiveSection } from "../hooks/useActiveSection";
import { AuthNavMenu } from "./AuthNavMenu";
import { HOME_PATH } from "../constants/routes";

interface SiteNavbarProps {
  /** Hide section links (Features, FAQ, etc.) — for auth/onboarding pages */
  minimal?: boolean;
  /** Optional content shown before the auth menu (e.g. sign-up prompt) */
  extraContent?: ReactNode;
}

const NavbarWithPopup = ({ minimal = false, extraContent }: SiteNavbarProps) => {
  const basename = process.env.PUBLIC_URL || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === "/" || location.pathname === "/main_layout";
  const isAboutPage = location.pathname.startsWith("/about");
  const isInsightsPage = location.pathname.startsWith("/insights");
  const isExperiencesPage = location.pathname.startsWith("/experiences");
  const isJoinUsPage = location.pathname.startsWith("/join-us");
  const isReImagineLitePage = location.pathname.startsWith("/reimagine-lite");

  // Define all section ID arrays first
  const homePageSectionIds = ["features", "faq"];

  const reimagineLiteSectionIds = ["reimagine-lite"];

  const aboutPageSectionIds = ["values", "story", "leadership", "locations"];

  const joinUsSectionIds = [
    "culture",
    "axl-academy",
    "open-positions",
    "apply-now",
  ];

  // Track which section is currently visible on main page
  const activeSection = useActiveSection(homePageSectionIds);

  // Track ReImagine Lite sections
  const activeReImagineSection = useActiveSection(reimagineLiteSectionIds);

  // Track about page sections
  const activeAboutSection = useActiveSection(aboutPageSectionIds);

  // Track join us page sections
  const activeJoinUsSection = useActiveSection(joinUsSectionIds);

  const handleSectionScroll = (e, href) => {
    // Only apply center scroll on larger screens (desktop)
    if (window.innerWidth < 786) {
      return; // Let default behavior handle it for mobile/tablet
    }

    e.preventDefault();
    const sectionId = href.replace("#", "");

    // Otherwise, scroll the main page
    scrollToMainSection(sectionId);
  };

  const scrollToMainSection = (sectionId) => {
    const section = document.getElementById(sectionId);

    if (section) {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top + window.scrollY;
      const sectionBottom = rect.bottom + window.scrollY;
      const sectionHeight = sectionBottom - sectionTop;
      const screenHeight = window.innerHeight;

      // Get the navbar height to account for fixed positioning
      const navbar = document.querySelector(".navbar");
      const navbarHeight = navbar ? navbar.offsetHeight : 0;

      let targetScroll;

      // Special case: always center the story section
      if (sectionId === "story") {
        const sectionCenter = sectionTop + sectionHeight / 2;
        const screenCenter = screenHeight / 2;
        targetScroll = sectionCenter - screenCenter;
      } else if (sectionHeight < screenHeight) {
        // If section fits on screen, center it
        const sectionCenter = sectionTop + sectionHeight / 2;
        const screenCenter = screenHeight / 2;
        targetScroll = sectionCenter - screenCenter;
      } else {
        // If section is taller than screen, position its start at the top, accounting for navbar
        targetScroll = sectionTop - navbarHeight;
      }

      window.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: "smooth",
      });
    }
  };

  const homePageSections = [
    {
      label: "ReImagine Lite",
      href: "/reimagine-lite",
      sectionId: "reimagine-lite",
      isExternal: true,
    },
    { label: "features", href: "#features", sectionId: "features" },
    { label: "faq", href: "#faq", sectionId: "faq" },
  ];

  const reimagineLiteSections = [
    {
      label: "ReImagine Lite",
      href: "#reimagine-lite",
      sectionId: "reimagine-lite",
      isExternal: false,
    },
    {
      label: "ReImagine 2.0",
      href: "/",
      sectionId: "reimagine-2.0",
      isExternal: true,
    },
  ];

  const traxccelAISections = [
    { label: "partnerships", href: "#partnerships", sectionId: "partnerships" },
    { label: "AI services", href: "#ai-services", sectionId: "ai-services" },
    { label: "AI solutions", href: "#ai-solutions", sectionId: "ai-solutions" },
    { label: "AI Lab", href: "#ai-lab", sectionId: "ai-lab" },
  ];

  const aboutPageSections = [
    { label: "values", href: "#values", sectionId: "values" },
    { label: "story", href: "#story", sectionId: "story" },
    { label: "leadership", href: "#leadership", sectionId: "leadership" },
    { label: "locations", href: "#locations", sectionId: "locations" },
  ];
  const joinUsPageSections = [
    { label: "culture", href: "#culture", sectionId: "culture" },
    { label: "axlACADEMY", href: "#axl-academy", sectionId: "axl-academy" },
    {
      label: "open positions",
      href: "#open-positions",
      sectionId: "open-positions",
    },
    { label: "apply now", href: "#apply-now", sectionId: "apply-now" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Navbar */}
      <nav className={`custom-navbar ${isScrolled ? "scrolled" : ""} ${minimal ? "auth-page-nav" : ""}`}>
        <div className="navbar-container">
          {/* Logo with ReImagine 2.0 */}
          <div className="logo-wrapper">
            <Link to={HOME_PATH} className="logo" aria-label="Go to home page" title="Go to home page">
              <img src={`${basename}/images/logo.webp`} alt="Traxccel Logo" />
            </Link>
            <div className="vertical-separator"></div>
            <Link to={HOME_PATH} className="reimagine-brand" aria-label="Go to home page" title="Go to home page">
              <h2>
                <span style={{ fontWeight: "300" }}>Re</span>
                <span style={{ fontWeight: "500" }}>Imagine</span>
                <span
                  style={{
                    fontWeight: "300",
                    fontStyle: "italic",
                    marginLeft: "4px",
                  }}
                >
                  2.0
                </span>
              </h2>
            </Link>
          </div>

          {/* Horizontal Nav Bar - Different for Each Page */}
          {!minimal && isHomePage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={section.href}
                      onClick={(e) => {
                        if (!section.isExternal) {
                          handleSectionScroll(e, section.href);
                        }
                      }}
                      className={
                        activeSection === section.sectionId ? "active" : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!minimal && isReImagineLitePage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {reimagineLiteSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={section.href}
                      onClick={(e) => {
                        if (!section.isExternal) {
                          handleSectionScroll(e, section.href);
                        }
                      }}
                      className={
                        section.sectionId === "reimagine-lite"
                          ? "reimagine-lite-active"
                          : ""
                      }
                      style={
                        activeReImagineSection === section.sectionId &&
                        section.sectionId === "reimagine-lite"
                          ? { color: "#db6d53" }
                          : {}
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!minimal && isAboutPage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {aboutPageSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={section.href}
                      onClick={(e) => handleSectionScroll(e, section.href)}
                      className={
                        activeAboutSection === section.sectionId ? "active" : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!minimal && isInsightsPage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={`/${section.href}`}
                      onClick={(e) => {
                        e.preventDefault();
                        // Navigate to home with hash
                        window.location.href = `/${section.href}`;
                      }}
                      className=""
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!minimal && isExperiencesPage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={`/${section.href}`}
                      onClick={(e) => {
                        e.preventDefault();
                        // Navigate to home with hash
                        window.location.href = `/${section.href}`;
                      }}
                      className=""
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!minimal && isJoinUsPage && (
            <div className="horizontal-nav">
              <ul className="horizontal-nav-menu">
                {joinUsPageSections.map((section) => (
                  <li key={section.label} className="horizontal-nav-item">
                    <a
                      href={section.href}
                      onClick={(e) => handleSectionScroll(e, section.href)}
                      className={
                        activeJoinUsSection === section.sectionId
                          ? "active"
                          : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {extraContent && (
            <div className="navbar-extra-content">{extraContent}</div>
          )}

          {!minimal && <AuthNavMenu />}

          {/* Mobile Menu Button - Visible only on mobile/tablet */}
          {!minimal && (
          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isMobileMenuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </>
              ) : (
                <>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </>
              )}
            </svg>
          </button>
          )}
        </div>

        {/* Mobile Menu Dropdown */}
        {!minimal && (
          <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
            {isHomePage && (
              <ul className="mobile-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={section.href}
                      onClick={(e) => {
                        if (!section.isExternal) {
                          handleSectionScroll(e, section.href);
                        }
                        setIsMobileMenuOpen(false);
                      }}
                      className={
                        activeSection === section.sectionId ? "active" : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}

            {isReImagineLitePage && (
              <ul className="mobile-nav-menu">
                {reimagineLiteSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={section.href}
                      onClick={(e) => {
                        if (!section.isExternal) {
                          handleSectionScroll(e, section.href);
                        }
                        setIsMobileMenuOpen(false);
                      }}
                      className={
                        section.sectionId === "reimagine-lite"
                          ? "reimagine-lite-active"
                          : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}

            {isAboutPage && (
              <ul className="mobile-nav-menu">
                {aboutPageSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={section.href}
                      onClick={(e) => {
                        handleSectionScroll(e, section.href);
                        setIsMobileMenuOpen(false);
                      }}
                      className={
                        activeAboutSection === section.sectionId ? "active" : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}

            {isInsightsPage && (
              <ul className="mobile-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={`/${section.href}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/${section.href}`;
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}

            {isExperiencesPage && (
              <ul className="mobile-nav-menu">
                {homePageSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={`/${section.href}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/${section.href}`;
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}

            {isJoinUsPage && (
              <ul className="mobile-nav-menu">
                {joinUsPageSections.map((section) => (
                  <li key={section.label}>
                    <a
                      href={section.href}
                      onClick={(e) => {
                        handleSectionScroll(e, section.href);
                        setIsMobileMenuOpen(false);
                      }}
                      className={
                        activeJoinUsSection === section.sectionId
                          ? "active"
                          : ""
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
                <AuthNavMenu
                  mobile
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </ul>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default NavbarWithPopup;
